import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Typography, Box, Card, CardContent, Button, Grid } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { Event, School, People, Class, CheckCircle, Stars, Assignment, HowToReg, Info, Send, Public } from '@mui/icons-material';

const ScholarshipDetails = () => {
  const { state } = useLocation();
  const { scholarship } = state || {};
  const controls = {
    header: useAnimation(),
    card1: useAnimation(),
    card2: useAnimation(),
    card3: useAnimation(),
    card4: useAnimation(),
    button: useAnimation(),
  };

  useEffect(() => {
    const animate = async () => {
      await controls.header.start({ opacity: 1, y: 0, transition: { duration: 0.8 } });
      await Promise.all([
        controls.card1.start({ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 100 } }),
        controls.card2.start({ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 100, delay: 0.2 } }),
        controls.card3.start({ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 100, delay: 0.4 } }),
        controls.card4.start({ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 100, delay: 0.6 } }),
      ]);
      controls.button.start({
        scale: [1, 1.05, 1],
        transition: { repeat: Infinity, duration: 1.6 },
      });
    };
    animate();
  }, [controls]);

  const handlePressLink = () => {
    window.open(scholarship.officialLink, '_blank');
  };

  const renderDetailRow = (IconComponent, label, value) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ bgcolor: '#6200ee', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
        <IconComponent sx={{ color: '#fff', fontSize: 20 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.75rem', color: '#888', mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1rem', color: '#2c3e50', fontWeight: 500 }}>{value}</Typography>
      </Box>
    </Box>
  );

  if (!scholarship) return <Typography>Error: No scholarship data</Typography>;

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={controls.header}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 1 }}>
            {scholarship.title}
          </Typography>
          <Typography variant="h6" sx={{ color: '#6200ee', fontWeight: 600 }}>
            {scholarship.hostOrganization}
          </Typography>
        </Box>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={controls.card1}>
        <Card sx={{ mb: 2, bgcolor: '#fff', borderRadius: 3, boxShadow: '0 4px 8px rgba(98,0,238,0.1)' }}>
          <CardContent>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#6200ee', mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
              Key Information
            </Typography>
            {renderDetailRow(Event, 'Deadline', new Date(scholarship.deadline).toLocaleDateString())}
            {renderDetailRow(School, 'Type', scholarship.type)}
            {renderDetailRow(People, 'Level', scholarship.level)}
            {renderDetailRow(Public, 'Countries', scholarship.eligibleCountries)}
            {renderDetailRow(Class, 'Fields', scholarship.fieldsOfStudy)}
          </CardContent>
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={controls.card2}>
        <Card sx={{ mb: 2, bgcolor: '#fff', borderRadius: 3, boxShadow: '0 4px 8px rgba(98,0,238,0.1)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircle sx={{ color: '#6200ee', fontSize: 24, mr: 1 }} />
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2c3e50' }}>
                Eligibility Criteria
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '1rem', color: '#555', lineHeight: 1.5 }}>
              {scholarship.eligibilityCriteria}
            </Typography>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={controls.card3}>
        <Card sx={{ mb: 2, bgcolor: '#fff', borderRadius: 3, boxShadow: '0 4px 8px rgba(98,0,238,0.1)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Stars sx={{ color: '#6200ee', fontSize: 24, mr: 1 }} />
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2c3e50' }}>
                Benefits
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '1rem', color: '#555', lineHeight: 1.5, mb: scholarship.duration ? 2 : 0 }}>
              {scholarship.benefits}
            </Typography>
            {scholarship.duration && (
              <Box sx={{ bgcolor: '#f0e8ff', p: 2, borderRadius: 2 }}>
                <Typography sx={{ fontSize: '0.9375rem', color: '#6200ee' }}>
                  <strong>Duration: </strong>{scholarship.duration}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={controls.card4}>
        <Card sx={{ mb: 2, bgcolor: '#fff', borderRadius: 3, boxShadow: '0 4px 8px rgba(98,0,238,0.1)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment sx={{ color: '#6200ee', fontSize: 24, mr: 1 }} />
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2c3e50' }}>
                Application Process
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '1rem', color: '#555', lineHeight: 1.5, mb: scholarship.selectionProcess ? 2 : 0 }}>
              {scholarship.applicationProcess}
            </Typography>
            {scholarship.selectionProcess && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
                  <HowToReg sx={{ color: '#6200ee', fontSize: 24, mr: 1 }} />
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2c3e50' }}>
                    Selection Process
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1rem', color: '#555', lineHeight: 1.5 }}>
                  {scholarship.selectionProcess}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <motion.div animate={controls.button}>
        <Button
          variant="contained"
          sx={{
            my: 4,
            bgcolor: '#6200ee',
            borderRadius: 30,
            py: 2,
            px: 4,
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '1.125rem',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 4px 8px rgba(98,0,238,0.3)',
            '&:hover': { bgcolor: '#3700b3' },
          }}
          startIcon={<Send />}
          onClick={handlePressLink}
        >
          Apply Now
        </Button>
      </motion.div>

      {scholarship.additionalNotes && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={controls.card4}>
          <Card sx={{ mb: 2, bgcolor: '#fff', borderRadius: 3, boxShadow: '0 4px 8px rgba(98,0,238,0.1)', borderLeft: '4px solid #6200ee' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Info sx={{ color: '#6200ee', fontSize: 24, mr: 1 }} />
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2c3e50' }}>
                  Additional Notes
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '1rem', color: '#555', lineHeight: 1.5 }}>
                {scholarship.additionalNotes}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography sx={{ color: '#666', mb: 2 }}>Need help with your application?</Typography>
        <Button
          variant="outlined"
          sx={{
            borderColor: '#6200ee',
            color: '#6200ee',
            borderRadius: 20,
            py: 1,
            px: 3,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Contact Support
        </Button>
      </Box>
    </Container>
  );
};

export default ScholarshipDetails;